import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquareText, X, Pencil, Loader2, Check, RotateCcw, ChevronLeft } from 'lucide-react';
import { getPrompts, setPromptConfig, setProductPrompt, setModelPrompt } from '../api';

function Toggle({ on, onClick }) {
  return (
    <button onClick={onClick}
      className={`relative w-14 h-7 shrink-0 rounded-full transition-colors ${on ? 'bg-orange' : 'bg-field border border-field-border'}`}>
      <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${on ? 'left-7' : 'left-0.5'}`} />
    </button>
  );
}

function ModelPromptModal({ model, onClose, onSaved, flash }) {
  const [text, setText] = useState(model.system_prompt || '');
  const [busy, setBusy] = useState(false);
  const save = async (value) => {
    setBusy(true);
    try {
      await setModelPrompt({ model: model.id, system_prompt: value });
      flash(value.trim() ? `Custom prompt set for ${model.id}` : `${model.id} reset to default`);
      onSaved();
    } catch (e) { flash(e.response?.data?.detail || 'Save failed', 'error'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl">
        <div className="flex justify-between items-center p-6 pb-4 border-b border-panel-border">
          <div>
            <h2 className="text-lg font-semibold text-fg">System prompt</h2>
            <p className="text-fg-muted text-xs mt-0.5 font-mono">{model.id}{model.provider ? ` · ${model.provider}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg p-1 rounded-lg hover:bg-field"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto">
          <p className="text-fg-muted text-xs mb-2">Leave empty to use the product built-in prompt. Variables: <code className="font-mono">{'{model}'}</code> <code className="font-mono">{'{date}'}</code>.</p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={14}
            placeholder="(empty → uses the product built-in prompt)"
            className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm font-mono resize-y" />
        </div>
        <div className="flex items-center justify-between gap-3 p-4 border-t border-panel-border">
          <button onClick={() => save('')} disabled={busy} className="flex items-center gap-1.5 px-3 py-2 text-fg-muted hover:text-fg text-sm rounded-lg hover:bg-field transition-colors"><RotateCcw className="w-4 h-4" />Reset to default</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-field border border-field-border text-fg rounded-lg hover:border-orange text-sm transition-colors">Cancel</button>
            <button onClick={() => save(text)} disabled={busy} className="px-4 py-2 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg text-sm disabled:opacity-50 transition-colors">{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const PRODUCTS = [
  { key: 'latentcode', label: 'LatentCode' },
  { key: 'latentwork', label: 'LatentWork' },
];

export default function PromptManagement() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productTexts, setProductTexts] = useState({ latentcode: '', latentwork: '' });
  const [activeProduct, setActiveProduct] = useState('latentcode');
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);

  const flash = (text, type = 'success') => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getPrompts();
      setData(r.data);
      const pp = r.data.product_prompts || {};
      setProductTexts({
        latentcode: pp.latentcode?.custom || pp.latentcode?.builtin_default || '',
        latentwork: pp.latentwork?.custom || pp.latentwork?.builtin_default || '',
      });
    } catch { setMsg({ type: 'error', text: 'Failed to load prompts' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async () => {
    const v = !data.enabled;
    setData((d) => ({ ...d, enabled: v }));
    setSavingToggle(true);
    try {
      await setPromptConfig({ enabled: v });
      flash('Saved');
      await load();
    } catch (e) {
      flash(e.response?.data?.detail || 'Save failed', 'error');
      setData((d) => ({ ...d, enabled: !v }));
    } finally { setSavingToggle(false); }
  };

  const saveProductPrompt = async (product, value) => {
    setSavingProduct(true);
    try {
      await setProductPrompt({ product, system_prompt: value });
      flash(`${PRODUCTS.find(p => p.key === product)?.label} prompt ${value.trim() ? 'saved' : 'reset to built-in'}`);
      await load();
    } catch (e) { flash(e.response?.data?.detail || 'Save failed', 'error'); }
    finally { setSavingProduct(false); }
  };

  if (loading || !data) return <div className="max-w-5xl text-center text-fg-muted py-16">Loading…</div>;

  const activeProductData = (data.product_prompts || {})[activeProduct] || {};
  const activeText = productTexts[activeProduct] || '';
  const hasOverride = activeProductData.has_override || false;

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate('/settings')} className="flex items-center gap-1 text-sm text-fg-muted hover:text-fg mb-4">
        <ChevronLeft className="w-4 h-4" /> Platform Settings
      </button>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-fg flex items-center gap-2"><MessageSquareText className="w-6 h-6 text-orange" />Prompt Management</h1>
        <p className="text-fg-muted text-sm mt-1 max-w-2xl">The platform system prompt <span className="text-fg">replaces</span> whatever the client sends. Resolution order: per-model override → product prompt → product built-in. Variables <code className="font-mono">{'{model}'}</code> and <code className="font-mono">{'{date}'}</code> are filled per request.</p>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">Applies to <span className="text-fg">LatentCode and LatentWork only</span> — they identify themselves with the <code className="font-mono">X-Latent-Product</code> header. Direct API calls (curl, OpenAI SDKs, third-party integrations) get no platform prompt at all, including per-model overrides, and are billed under the <code className="font-mono">api</code> product.</p>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex justify-between items-center ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{msg.text}</span><button onClick={() => setMsg(null)} className="hover:opacity-70"><X className="w-4 h-4" /></button>
        </div>
      )}


      {/* enforcement toggle */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5 mb-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-fg font-semibold flex items-center gap-2">
            System prompt injection
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${data.enabled ? 'bg-green-500/10 text-green-400' : 'bg-field text-fg-muted'}`}>{data.enabled ? 'ON' : 'OFF'}</span>
          </div>
          <div className="text-fg-muted text-xs mt-0.5">{data.enabled ? "The product (or per-model) prompt replaces the client's system prompt on every request." : "Off — the client's own system prompt is used as-is."}</div>
        </div>
        <Toggle on={data.enabled} onClick={toggleEnabled} disabled={savingToggle} />
      </div>

      {/* per-product prompts */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <select value={activeProduct} onChange={(e) => setActiveProduct(e.target.value)}
              className="px-3 py-1.5 bg-field border border-field-border rounded-lg text-fg text-sm font-medium focus:outline-none focus:border-orange">
              {PRODUCTS.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
            </select>
            <span className="text-fg-muted text-xs">product prompt</span>
            {hasOverride
              ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange/10 text-orange">customized</span>
              : <span className="text-[10px] px-1.5 py-0.5 rounded bg-field text-fg-muted">built-in</span>}
          </div>
          <div className="flex gap-2">
            {hasOverride && (
              <button onClick={() => { setProductTexts((p) => ({ ...p, [activeProduct]: activeProductData.builtin_default || '' })); saveProductPrompt(activeProduct, ''); }}
                disabled={savingProduct} className="flex items-center gap-1.5 px-3 py-1.5 text-fg-muted hover:text-fg text-sm rounded-lg hover:bg-field transition-colors">
                <RotateCcw className="w-4 h-4" />Reset to built-in
              </button>
            )}
            <button onClick={() => saveProductPrompt(activeProduct, activeText)} disabled={savingProduct}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-orange hover:bg-orange-hover text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
              {savingProduct ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Save
            </button>
          </div>
        </div>
        <textarea value={activeText} onChange={(e) => setProductTexts((p) => ({ ...p, [activeProduct]: e.target.value }))} rows={20}
          className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm font-mono resize-y" />
      </div>

      {/* per-model overrides */}
      <div className="bg-panel border border-panel-border rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-panel-border flex items-center justify-between">
          <span className="text-fg font-medium text-sm">Per-model prompts</span>
          <span className="text-fg-muted text-xs">{data.models.filter((m) => m.has_override).length} customized · {data.models.length} models</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-fg-muted bg-field/40 border-b border-panel-border">
              <th className="px-5 py-3 font-medium">Model</th>
              <th className="px-3 py-3 font-medium">Provider</th>
              <th className="px-3 py-3 font-medium">Prompt</th>
              <th className="px-5 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {data.models.map((m) => (
              <tr key={m.id} className="border-b border-panel-border/50 last:border-0 hover:bg-field/30 transition-colors">
                <td className="px-5 py-3 text-fg font-mono">{m.id}</td>
                <td className="px-3 py-3 text-fg-muted">{m.provider || '—'}</td>
                <td className="px-3 py-3">
                  {m.has_override
                    ? <span className="inline-flex items-center gap-1.5 text-xs text-orange"><span className="w-1.5 h-1.5 rounded-full bg-orange" />Custom</span>
                    : <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted"><span className="w-1.5 h-1.5 rounded-full bg-fg-muted/40" />Default</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setEditing(m)} title="Edit system prompt" className="p-1.5 text-fg-muted hover:text-fg rounded-lg hover:bg-field transition-colors"><Pencil className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {data.models.length === 0 && <tr><td colSpan={4} className="text-center text-fg-muted py-10">No models configured.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <ModelPromptModal model={editing}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} flash={flash} />}
    </div>
  );
}
